import {
  catalogCategoryPath,
  catalogCategorySectionPath,
  catalogVendorPath,
  CATALOG_SECTION_NONE_SLUG,
  sectionSlugForNavigation,
} from './catalogRoutes';

/** Ключ в location.state — URL предыдущего шага каталога (pathname + search). */
export const CATALOG_BACK_STATE_KEY = 'catalogBack';

export function currentCatalogHref(location) {
  if (!location?.pathname) return '/solutions';
  return `${location.pathname}${location.search || ''}`;
}

export function getCatalogBack(locationState) {
  const url = locationState?.[CATALOG_BACK_STATE_KEY];
  if (typeof url !== 'string' || !url.startsWith('/')) return null;
  return url;
}

function pathnameFromHref(href) {
  const qIndex = href.indexOf('?');
  return qIndex >= 0 ? href.slice(0, qIndex) : href;
}

/** Страница вендора в каталоге: /solutions/:vendorSlug (без /:solutionSlug). */
export function isCatalogVendorHref(href) {
  return /^\/solutions\/[^/]+$/.test(pathnameFromHref(href));
}

/**
 * Цель для кнопки «Назад»: на странице вендора — родитель по URL (?category/?section),
 * иначе сохранённый catalogBack или inferCatalogParentHref.
 */
export function resolveCatalogBackTarget(currentHref, locationState, sections = []) {
  const inferred = inferCatalogParentHref(currentHref, sections);
  if (isCatalogVendorHref(currentHref)) {
    return inferred;
  }
  const fromState = getCatalogBack(locationState);
  if (!fromState || fromState === currentHref) {
    return inferred;
  }
  return fromState;
}

/** Синхронизация фильтров каталога из canonical URL (для кнопки «Назад»). */
export function parseCatalogHref(href, sections = []) {
  const qIndex = href.indexOf('?');
  const pathname = qIndex >= 0 ? href.slice(0, qIndex) : href;
  const params = new URLSearchParams(qIndex >= 0 ? href.slice(qIndex + 1) : '');

  const sectionMatch = pathname.match(/^\/catalog\/([^/]+)\/([^/]+)$/);
  if (sectionMatch) {
    const categoryFilter = decodeURIComponent(sectionMatch[1]);
    const sectionSlug = decodeURIComponent(sectionMatch[2]);
    let sectionFilter = 'all';
    if (sectionSlug === CATALOG_SECTION_NONE_SLUG) {
      sectionFilter = 'Без раздела';
    } else {
      const record = sections.find(
        (s) => s.category_id === categoryFilter && s.slug === sectionSlug,
      );
      if (record?.name) sectionFilter = record.name;
    }
    return {
      categoryFilter,
      sectionFilter,
      situationFilter: null,
      clearVendor: true,
    };
  }

  const categoryMatch = pathname.match(/^\/catalog\/([^/]+)$/);
  if (categoryMatch) {
    return {
      categoryFilter: decodeURIComponent(categoryMatch[1]),
      sectionFilter: 'all',
      situationFilter: null,
      clearVendor: true,
    };
  }

  if (pathname === '/solutions') {
    return {
      categoryFilter: params.get('category') || 'all',
      sectionFilter: params.get('section') || 'all',
      situationFilter: params.get('situation') || null,
      clearVendor: !params.get('vendor'),
    };
  }

  if (isCatalogVendorHref(href)) {
    return {
      categoryFilter: params.get('category') || 'all',
      sectionFilter: params.get('section') || 'all',
      situationFilter: params.get('situation') || null,
      clearVendor: false,
    };
  }

  return {
    categoryFilter: 'all',
    sectionFilter: 'all',
    situationFilter: null,
    clearVendor: true,
  };
}

export function withCatalogBack(existingState, backUrl) {
  if (!backUrl || typeof backUrl !== 'string' || !backUrl.startsWith('/')) {
    return existingState ?? {};
  }
  return { ...(existingState ?? {}), [CATALOG_BACK_STATE_KEY]: backUrl };
}

/**
 * Родительский URL на один шаг выше по иерархии каталога.
 * Каталог → категория → раздел → вендор → решение; отдельно: ситуация → вендор → решение.
 */
export function inferCatalogParentHref(href, sections = []) {
  const qIndex = href.indexOf('?');
  const pathname = qIndex >= 0 ? href.slice(0, qIndex) : href;
  const search = qIndex >= 0 ? href.slice(qIndex + 1) : '';
  const params = new URLSearchParams(search);

  const solutionMatch = pathname.match(/^\/solutions\/([^/]+)\/([^/]+)$/);
  if (solutionMatch) {
    return catalogVendorPath(solutionMatch[1], {
      categoryId: params.get('category') || 'all',
      sectionName: params.get('section') || 'all',
      situationId: params.get('situation') || null,
    });
  }

  const vendorMatch = pathname.match(/^\/solutions\/([^/]+)$/);
  if (vendorMatch) {
    const situation = params.get('situation');
    if (situation) {
      return `/solutions?situation=${encodeURIComponent(situation)}`;
    }
    const cat = params.get('category');
    const sec = params.get('section');
    if (cat && cat !== 'all' && sec && sec !== 'all') {
      const record = sections.find((s) => s.category_id === cat && s.name === sec);
      const slug = sectionSlugForNavigation(sec, record);
      if (slug) return catalogCategorySectionPath(cat, slug);
    }
    if (cat && cat !== 'all') return catalogCategoryPath(cat);
    return '/solutions';
  }

  const sectionMatch = pathname.match(/^\/catalog\/([^/]+)\/([^/]+)$/);
  if (sectionMatch) return catalogCategoryPath(sectionMatch[1]);

  const categoryMatch = pathname.match(/^\/catalog\/([^/]+)$/);
  if (categoryMatch) return '/solutions';

  if (pathname === '/solutions' && params.get('situation')) return '/solutions';

  return '/solutions';
}
